import React, { useEffect, useRef } from 'react';
import { t } from '@lingui/macro';

import { Box, Flex, Heading, Image, Paragraph, Text } from '@rover/kibble/core';
import type { EnhancedPersonSitterProfile } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfile';
import fireDataLayerEvent from '@rover/utilities/fireDataLayerEvent';

import { SitterProfileVersion } from '../../hooks/useRevampProfileContext';
import CatProviderProfileModal from '../CatProviderProfileModal/CatProviderProfileModal';

type Props = {
  sitterInfo: EnhancedPersonSitterProfile;
  userOpk?: string;
  pageVariant?: SitterProfileVersion;
};

const CatCareExperienceContainer = ({
  sitterInfo,
  userOpk,
  pageVariant,
}: Props): JSX.Element | null => {
  const { catProviderProfile, firstName } = sitterInfo;
  const firedEventOnLoad = useRef<boolean>(false);
  useEffect(() => {
    if (!firedEventOnLoad.current) {
      fireDataLayerEvent({
        event: 'view-hybrid-care-content-primary',
        personId: userOpk,
        sitterId: sitterInfo.opk,
      });
      firedEventOnLoad.current = true;
    }
  }, [userOpk, sitterInfo.opk]);

  if (!catProviderProfile) return null;

  const isPluralAccount = sitterInfo.providerProfile?.isPluralAccount ?? false;
  const catCareDescription = isPluralAccount
    ? t`${firstName} also care for cats. Learn about their cat-specific care experience.`
    : t`${firstName} also cares for cats. Learn about their cat-specific care experience.`;

  return (
    <Box sx={{ paddingTop: ['32px', '40px'] }}>
      <Heading size="300" mb="4x">
        {t`Cat care experience`}
      </Heading>
      <Flex
        flexDirection="row"
        alignItems="flex-start"
        justifyContent="space-between"
        sx={{
          fontSize: 'initial',
          fontWeight: 'initial',
          lineHeight: 'initial',
        }}
        width="100%"
      >
        <Flex flexDirection="row" alignItems="center" pb="3x">
          <Box justifyContent="flex-start" mr="3x" width="48px" height="48px">
            <Image
              height="48px"
              width="48px"
              data-testid="cat-provider-profile-image"
              description={t`Profile image`}
              src={catProviderProfile.profilePhoto.medium}
              borderRadius="round"
            />
          </Box>
          <Flex flexDirection="column" flex="1 0 0">
            <Text size="200" fontWeight="semibold">
              {catProviderProfile.headline}
            </Text>
            <Paragraph size="200" mb="0x">
              {catCareDescription}
            </Paragraph>
          </Flex>
        </Flex>
      </Flex>
      <CatProviderProfileModal sitter={sitterInfo} userOpk={userOpk} pageVariant={pageVariant} />
    </Box>
  );
};

export default CatCareExperienceContainer;
